import { useMemo, useRef, useCallback } from "react"
import { useFrame } from "@react-three/fiber"
import * as THREE from "three"
import type { SensorData } from "@/contexts/data-context"
import { useScene } from "@/contexts/scene-context"

interface HeatmapVisualizationProps {
  sensors: SensorData[]
  resolution?: number
  height?: number
  opacity?: number
}

export default function HeatmapVisualization({
  sensors,
  resolution = 30,
  height = 0.05,
  opacity = 0.6,
}: HeatmapVisualizationProps) {
  const heatmapRef = useRef<THREE.Mesh>(null)
  const { sceneState } = useScene()
  const frameCount = useRef(0)
  const lastUpdateTime = useRef(0)

  // Create heatmap data - memoized for performance
  const { geometry, minValue, maxValue } = useMemo(() => {
    // Create a grid of points
    const size = 12 // Size of the grid
    const geo = new THREE.PlaneGeometry(size, size, resolution - 1, resolution - 1)
    geo.rotateX(-Math.PI / 2) // Make it horizontal

    // If no sensors, return empty geometry with default values
    if (sensors.length === 0) {
      return { geometry: geo, minValue: 0, maxValue: 1 }
    }

    // Filter only online sensors for better performance
    const onlineSensors = sensors.filter(sensor => sensor.isOnline)

    // Get vertex positions
    const positions = geo.attributes.position.array as Float32Array
    const values: number[] = []

    // Calculate value at each vertex based on sensor proximity
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i]
      const z = positions[i + 2]

      let totalValue = 0
      let totalWeight = 0

      for (const sensor of onlineSensors) {
        const [sx, sy, sz] = sensor.position
        const distance = Math.sqrt((x - sx) ** 2 + (z - sz) ** 2)

        // Inverse square law for influence with a maximum range
        const maxRange = 8
        if (distance < maxRange) {
          // Smoother falloff function
          const weight = Math.pow(1 - Math.min(1, distance / maxRange), 2)
          totalValue += sensor.value * weight
          totalWeight += weight
        }
      }

      const value = totalWeight > 0 ? totalValue / totalWeight : 0
      values.push(value)
    }

    // Find min and max values
    const min = Math.min(...values)
    const max = Math.max(...values)

    // If min equals max (all values are the same), add a small difference to avoid division by zero
    const effectiveMin = min
    const effectiveMax = min === max ? max + 0.001 : max

    // Create colors based on values
    const colors: number[] = []

    for (let i = 0; i < values.length; i++) {
      const value = values[i]
      const normalizedValue = (value - effectiveMin) / (effectiveMax - effectiveMin || 1)

      // Color gradient: blue (cold) to red (hot)
      const color = new THREE.Color()

      // Blue to green to red gradient
      if (normalizedValue < 0.5) {
        // Blue to green (0-0.5)
        color.setRGB(0, normalizedValue * 2, 1 - normalizedValue * 2)
      } else {
        // Green to red (0.5-1)
        color.setRGB((normalizedValue - 0.5) * 2, 1 - (normalizedValue - 0.5) * 2, 0)
      }

      colors.push(color.r, color.g, color.b)
    }

    // Apply colors to geometry
    geo.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3))

    return { geometry: geo, minValue: min, maxValue: max }
  }, [sensors, resolution])

  // Optimized animation function
  const updateHeatmapAnimation = useCallback((time: number) => {
    if (!heatmapRef.current || !heatmapRef.current.geometry.attributes.position) return
    
    const positions = heatmapRef.current.geometry.attributes.position.array as Float32Array

    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i]
      const z = positions[i + 2]

      // Original y position is 0 since it's a flat plane
      // Add a subtle wave effect
      positions[i + 1] = Math.sin(time * 0.5 + x * 0.5 + z * 0.5) * 0.03
    }

    heatmapRef.current.geometry.attributes.position.needsUpdate = true
  }, [])

  // Animate heatmap - only if not in low quality mode with reduced frequency
  useFrame((state) => {
    frameCount.current++
    const time = state.clock.getElapsedTime()
    
    // Only update animation in medium+ quality modes
    if (sceneState.renderQuality === "low") return
    
    // Reduce update frequency based on quality settings
    const updateFrequency = sceneState.renderQuality === "medium" ? 3 : 2
    if (frameCount.current % updateFrequency !== 0) return
    
    // Throttle updates to avoid excessive computation
    if (time - lastUpdateTime.current < 0.1) return
    
    lastUpdateTime.current = time
    updateHeatmapAnimation(time)
  })

  // Only render if we have online temperature sensors
  if (sensors.length === 0 || sensors.filter(s => s.isOnline).length === 0) return null

  return (
    <mesh ref={heatmapRef} position={[0, height, 0]}>
      <primitive object={geometry} />
      <meshBasicMaterial 
        vertexColors 
        transparent 
        opacity={opacity} 
        side={THREE.DoubleSide} 
        depthWrite={false}
        fog={false}
      />
    </mesh>
  )
}
